import { parseMapLinePayload, parseMapPinPayload, type MapLineLink, type MapPinLink } from './mapLinkPayload'

/**
 * Where a natlinkedin share link code leads on web.
 *
 * The link service holds each link's payload and serves it publicly to anyone
 * with the code (`/api/v1/link/<code>`, CORS-open): the code IS the access, so
 * reading it needs no credential and reaches no WeHunt data beyond what the
 * link already carries. That is why this is a plain fetch to another origin
 * rather than a call through `/api`, and why it is no Firebase data SDK.
 *
 * This app owns hunt-replay links and shared map pins and lines. A link of any
 * other scope is content only the app can open, so it goes to Natlink's page that
 * says so and asks the visitor to open the link on their phone.
 */
export type ShareLinkDestination =
  | { kind: 'sharedReplay'; shareId: string }
  | { kind: 'huntReplay'; huntId: string }
  | { kind: 'mapPin'; pin: MapPinLink }
  | { kind: 'mapLine'; line: MapLineLink }
  | { kind: 'elsewhere'; url: string }
  | { kind: 'invalid' }
  | { kind: 'unreachable' }

/**
 * Natlink's page for a link that must be opened on a phone — the link service
 * owner's choice for every scope a web app does not handle.
 */
export const APP_ONLY_LINK_PAGE = 'https://natlink.se/language/en/deeplink-fallback/'

/**
 * The read-only map preview the link service sent browsers to before this app
 * took them. Still the answer when no link domain is configured: without one
 * the code cannot be read, so its scope is unknown, and this page was built to
 * show whatever the link holds.
 */
const legacyPreview = (code: string) =>
  `https://tanger.tracker.fi/preview/wh?ntlkd=${encodeURIComponent(code)}`

const REPLAY_SCOPE = 'hunt_replay'
const MAP_PIN_SCOPE = 'map_pin'
const MAP_LINE_SCOPE = 'map_line'

type LinkPayload = Record<string, unknown>

/** What one domain said about a code. */
type Lookup =
  | { kind: 'found'; data: LinkPayload }
  /** The domain does not know the code. */
  | { kind: 'miss' }
  /** No usable answer at all — down, or failing. */
  | { kind: 'down' }
  /** The domain claims the code but its answer cannot be read. */
  | { kind: 'garbled' }

async function lookUp(domain: string, code: string, fetchFn: typeof fetch): Promise<Lookup> {
  let res: Response
  try {
    res = await fetchFn(`https://${domain}/api/v1/link/${encodeURIComponent(code)}`)
  } catch {
    // A network failure is an outcome here, not a fault: the caller reports it as
    // "unreachable" and the page offers a retry.
    return { kind: 'down' }
  }
  // A server error, a timeout or throttling is a service that could not answer
  // now, not a code it does not know: the visitor gets a retry, not "expired".
  if (res.status >= 500 || res.status === 408 || res.status === 429) return { kind: 'down' }
  if (!res.ok) return { kind: 'miss' }
  let body: unknown
  try {
    body = await res.json()
  } catch {
    // A 200 that is not JSON is a link nobody can follow; retrying will not change
    // what the service holds, so the visitor is told the link does not work.
    return { kind: 'garbled' }
  }
  const data = isObject(body) ? body.data : undefined
  // A known code whose payload is missing or not an object carries no hunt to
  // open, so it is not one of ours: it goes where every non-replay link goes,
  // which is the page the link service itself would have shown.
  return { kind: 'found', data: isObject(data) ? data : {} }
}

function isObject(value: unknown): value is LinkPayload {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function destinationOf(data: LinkPayload): ShareLinkDestination {
  // A map payload this page cannot draw is a broken link, not a reason to send
  // the visitor to another site that would have to fail on it too.
  if (data.scope === MAP_PIN_SCOPE) {
    const pin = parseMapPinPayload(data)
    return pin === null ? { kind: 'invalid' } : { kind: 'mapPin', pin }
  }
  if (data.scope === MAP_LINE_SCOPE) {
    const line = parseMapLinePayload(data)
    return line === null ? { kind: 'invalid' } : { kind: 'mapLine', line }
  }
  if (data.scope !== REPLAY_SCOPE) return { kind: 'elsewhere', url: APP_ONLY_LINK_PAGE }
  if (typeof data.shareId === 'string' && data.shareId !== '') {
    return { kind: 'sharedReplay', shareId: data.shareId }
  }
  if (typeof data.huntId === 'string' && data.huntId !== '') {
    return { kind: 'huntReplay', huntId: data.huntId }
  }
  return { kind: 'invalid' }
}

export async function resolveShareLink(
  code: string,
  domains: readonly string[],
  fetchFn: typeof fetch = fetch,
): Promise<ShareLinkDestination> {
  // With nothing configured this app cannot read any link, so it behaves exactly
  // as the link service did before it: an unset deployment variable is never
  // worse than today.
  if (domains.length === 0) return { kind: 'elsewhere', url: legacyPreview(code) }

  let everyDomainAnswered = true
  for (const domain of domains) {
    const answer = await lookUp(domain, code, fetchFn)
    if (answer.kind === 'down') {
      everyDomainAnswered = false
      continue
    }
    // Codes are per domain: a miss here is not a miss everywhere.
    if (answer.kind === 'miss') continue
    if (answer.kind === 'garbled') return { kind: 'invalid' }
    return destinationOf(answer.data)
  }
  // Only a miss on EVERY domain proves the link is gone; while one could not be
  // asked, it may be the one that holds the code, so the visitor gets a retry.
  return everyDomainAnswered ? { kind: 'invalid' } : { kind: 'unreachable' }
}
